import React, { useState } from "react";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/** Tiny "Report an error" affordance for a Today-in-History line: opens a small form, posts to /api/reports (emails the admin). */
export const ReportLine = ({ line, ddmm }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reports", { text: line.text, date_dd_mm: ddmm, kind: line.kind, year: Number.isFinite(Number(line.year)) ? Number(line.year) : null, message, email, page: window.location.href, event_id: /^(ev|sp|b|d)-/.test(line.id || "") ? line.id.replace(/^(ev|sp|b|d)-/, "") : "" });
      toast.success("Thank you — we check every report within seven days");
      setOpen(false); setMessage(""); setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the report");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Report an error in this fact" title="Report an error" data-testid={`${line.testid}-report`}
        className="w-6 h-6 rounded-full border border-[#e5e7eb] bg-white text-ink-muted hover:border-[#7a1a1d] hover:text-[#7a1a1d] inline-flex items-center justify-center transition-colors">
        <Flag className="w-3 h-3" />
      </button>
      {open && (
        <div data-testid="report-modal" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-ink/50 p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full sm:max-w-md bg-paper-light rounded-t-2xl sm:rounded-2xl p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">Report an error</p>
                <h3 className="mt-1 text-lg font-bold text-ink leading-snug">What's wrong with this entry?</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="report-close" className="w-9 h-9 rounded-full hover:bg-paper flex items-center justify-center text-ink"><X className="w-5 h-5" /></button>
            </div>
            <blockquote data-testid="report-fact" className="mt-3 border-l-4 border-gold bg-paper px-4 py-2 text-[14px] text-ink">{line.text}</blockquote>
            <textarea data-testid="report-message" required minLength={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Tell us what's wrong — wrong date, wrong year, wrong name, or a source we should check." className="mt-3 w-full rounded-xl border border-[#e5e7eb] bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/40" />
            <input data-testid="report-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email (optional, if you'd like a reply)" className="mt-2 w-full h-10 rounded-xl border border-[#e5e7eb] bg-white px-3 text-sm text-ink focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/40" />
            <button type="submit" disabled={busy} data-testid="report-submit" className="mt-4 w-full h-11 rounded-full bg-ink text-white font-semibold text-[14px] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">{busy ? "Sending…" : "Send report"}</button>
            <p className="mt-2 text-[11px] text-ink-muted">Reports go straight to the editor. See our <a href="/editorial-policy" className="font-semibold text-gold hover:underline">editorial policy</a>.</p>
          </form>
        </div>
      )}
    </>
  );
};
